/**
 * use-conversation-promise — S2 PR-b (Corrente de Compromissos, superfície).
 *
 * Alimenta o `CommitmentBanner` (pouso com propósito, design §4): o
 * compromisso ATENDENTE/IA→CLIENTE mais antigo AINDA ABERTO
 * (`Pendency.source='conversation-promise'`, status `open`/`in_progress`)
 * nesta conversa.
 *
 * **Reuso, sem endpoint novo** (design §4 — "verificar na fonte antes de
 * criar endpoint"): consome o MESMO `GET /v1/conversations/:id/pendencies`
 * já usado por `usePendencies` (mini-cards do painel direito), filtrando
 * client-side por `source`/`status` (o endpoint só aceita 1 valor de
 * `status`, e aqui precisamos de 2 — `open` OU `in_progress`).
 *
 * **Gate do fetch pela capability `conversation-commitments`** (design §4),
 * DIFERENTE da capability `pendencies` que o próprio endpoint já exige
 * server-side (gate pré-existente, intocado) — ambas precisam estar
 * concedidas para o banner aparecer. 403 (cap `pendencies` ausente no
 * servidor) degrada defensivamente para vazio (molde `usePendencies`).
 *
 * **customerReturns** (cliente voltou N× desde a promessa): computado
 * CLIENT-SIDE a partir de `conversation.messages` (já carregado no detail —
 * ZERO fetch adicional), diferente do enriquecimento da LISTAGEM (que conta
 * TODOS os inbounds via query própria no Engine). Limitação honesta: o
 * detail só traz as últimas ~30 mensagens — se a promessa for mais antiga
 * que a janela carregada, a contagem pode subestimar (nunca fabrica um
 * número maior que o real).
 */
import { useQuery } from '@tanstack/react-query';

import { useCapability } from '../../../hooks/use-capability';
import { api, ApiError } from '../../../lib/api-client';

import type { ConversationDetail } from '../../../lib/types';

export interface ConversationPromiseFixture {
  pendencyId: string;
  version: number;
  what: string;
  promisedAtIso: string;
  dueAtIso: string | null;
  overdue: boolean;
  customerReturns: number;
}

const OPEN_STATUSES = new Set(['open', 'in_progress']);
const COMMITMENT_SOURCE = 'conversation-promise';

export function useConversationPromise(conversation: ConversationDetail): {
  promise: ConversationPromiseFixture | null;
  isLoading: boolean;
} {
  const hasCapability = useCapability('conversation-commitments');
  const conversationId = conversation.id;
  const enabled = hasCapability && conversationId.length > 0;

  const query = useQuery({
    queryKey: ['pendencies', 'by-conversation', conversationId, 'commitment-banner'],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return await api.listPendenciesByConversation(conversationId);
      } catch (err) {
        // 403 (cap `pendencies` ausente no servidor) → degrada defensivamente.
        if (err instanceof ApiError && err.status === 403) return { data: [] };
        throw err;
      }
    },
  });

  if (!enabled || query.isLoading || query.isError) {
    return { promise: null, isLoading: enabled && query.isLoading };
  }

  const candidates = (query.data?.data ?? [])
    .filter((p) => p.source === COMMITMENT_SOURCE && OPEN_STATUSES.has(p.status))
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));

  // Compromisso mais antigo vence quando houver >1 (molde do enriquecimento
  // da listagem — design §1).
  const oldest = candidates[0];
  if (oldest === undefined) {
    return { promise: null, isLoading: false };
  }

  const nowMs = Date.now();
  const dueMs = oldest.deadline !== null ? Date.parse(oldest.deadline) : null;
  const overdue = dueMs !== null && dueMs < nowMs;
  const promisedAtMs = Date.parse(oldest.createdAt);
  const customerReturns = conversation.messages.filter(
    (m) =>
      m.direction === 'inbound' && m.origin === 'customer' && Date.parse(m.sentAt) > promisedAtMs,
  ).length;

  return {
    promise: {
      pendencyId: oldest.id,
      version: oldest.version,
      what: oldest.title,
      promisedAtIso: oldest.createdAt,
      dueAtIso: oldest.deadline,
      overdue,
      customerReturns,
    },
    isLoading: false,
  };
}
